import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { Navigate } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import SlAlert from '@shoelace-style/shoelace/dist/react/alert';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon';
import type SlAlertElement from '@shoelace-style/shoelace/dist/components/alert/alert';

import {
  useCohortProgress, useCourseDisplayNames, useDebounce, useLearningPathConfig,
} from '../../hooks';
import type { AllowedCourseItem, CohortMemberProgress } from '../../types';

import { useCohortLayoutContext } from './CohortLayout';
import MemberCard from './MemberCard';
import { ToastVariant } from './MemberActionsMenu';
import messages from './messages';
import { courseLabel, courseNumberFromId } from './utils';

/** Bootstrap-icon name for a member-action toast, by Shoelace alert variant. */
const toastIconFor = (variant: ToastVariant | undefined): string => {
  if (variant === 'success') {
    return 'check2-circle';
  }
  if (variant === 'primary') {
    return 'info-circle';
  }
  return 'exclamation-octagon';
};

/** DRF page size for the cohort endpoints. */
const PAGE_SIZE = 20;

/**
 * Members view. Owner/co-facilitator get a debounced server-side search,
 * per-member progress cards from `GET cohorts/<slug>/progress/` and the actions
 * menu; a plain member gets the read-only list from `GET cohorts/<slug>/members/`.
 * Both paginate with arrow controls; a non-member is redirected to Info.
 */
const CohortMembers: React.FC = () => {
  const intl = useIntl();
  const { detail, isMember, canModerate } = useCohortLayoutContext();
  const { config } = useLearningPathConfig();
  const displayNames = useCourseDisplayNames(canModerate ? detail.language : undefined);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<{ variant: ToastVariant; msg: string } | null>(null);

  const toastRef = useRef<SlAlertElement | null>(null);

  // Commit the typed query after a pause and reset to the first page, so each
  // keystroke doesn't fire a request or strand the user on a now-empty page.
  useDebounce(() => {
    setSearch(searchInput.trim());
    setPage(1);
  }, 300, [searchInput]);

  // A plain member reads `/members/` (the progress endpoint 403s for that role);
  // passing no slug for a non-member keeps the hook from firing a doomed request.
  const {
    rows, count, next, previous, loading, failed, refetch,
  } = useCohortProgress(isMember ? detail.slug : undefined, search, page, !canModerate);

  // Host the member-action toast here (not per-row): a removed member's card
  // unmounts on refetch, which would drop feedback living inside it.
  useDebounce(() => {
    if (toast?.msg && toastRef.current && 'toast' in toastRef.current) {
      toastRef.current?.toast();
    }
  }, 150, [toast]);

  const notify = useCallback((variant: ToastVariant, msg: string): void => {
    setToast({ variant, msg });
  }, []);

  // Refresh the list after a member mutation. Removing the last row of a
  // non-first page would leave it out of range on refetch — step back instead.
  const handleMutated = useCallback((wasRemoval: boolean): void => {
    if (wasRemoval && rows.length === 1 && page > 1) {
      setPage((current) => Math.max(1, current - 1));
    } else {
      refetch();
    }
  }, [rows.length, page, refetch]);

  // Chip labels resolve against the cohort language's learning-path config
  // (course ids are locale-specific).
  const courseLabels = useMemo(() => {
    const courses: AllowedCourseItem[] = config?.[detail.language]?.allowed_courses ?? [];
    const map = new Map<string, string>();
    courses.forEach((course) => {
      map.set(course.course_id, courseLabel(course, displayNames.get(course.course_id)));
    });
    return map;
  }, [config, detail.language, displayNames]);

  const resolveCourseLabel = (courseId: string): string => (
    courseLabels.get(courseId) ?? courseNumberFromId(courseId)
  );

  // Owner is pinned first per the design; everyone else keeps response order.
  const orderedRows = useMemo(() => {
    const owners: CohortMemberProgress[] = [];
    const others: CohortMemberProgress[] = [];
    rows.forEach((row) => (row.role === 'owner' ? owners : others).push(row));
    return [...owners, ...others];
  }, [rows]);

  if (!isMember) {
    return <Navigate to={`/cohorts/${detail.slug}/info`} replace />;
  }

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const showLoading = loading && rows.length === 0;
  const showError = !loading && failed;
  const showEmpty = !loading && !failed && rows.length === 0;

  return (
    <div className="cohort-members">
      {canModerate && (
        <>
          <SlAlert
            ref={toastRef}
            variant={toast?.variant ?? 'primary'}
            duration={5000}
            closable
          >
            <SlIcon slot="icon" name={toastIconFor(toast?.variant)} />
            <strong>{toast?.msg}</strong>
          </SlAlert>

          <div className="cohort-members-search">
            <input
              type="search"
              className="cohort-members-search-input"
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
              aria-label={intl.formatMessage(messages.searchPlaceholder)}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
        </>
      )}

      {showLoading && (
        <p className="cohort-members-status">{intl.formatMessage(messages.membersLoading)}</p>
      )}
      {showError && (
        <>
          <p className="form-error form-error-custom">{intl.formatMessage(messages.membersLoadError)}</p>
          <button
            type="button"
            className="cohort-members-retry-button"
            onClick={refetch}
          >
            {intl.formatMessage(messages.membersRetry)}
          </button>
        </>
      )}
      {showEmpty && (
        <p className="cohort-members-status">
          {intl.formatMessage(search ? messages.membersSearchEmpty : messages.membersEmpty)}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="cohort-members-list">
          {orderedRows.map((member) => (
            <MemberCard
              key={member.user_id}
              member={member}
              resolveCourseLabel={resolveCourseLabel}
              {...(canModerate && {
                slug: detail.slug,
                callerRole: detail.my_role,
                onMutated: handleMutated,
                onToast: notify,
              })}
            />
          ))}
        </ul>
      )}

      {rows.length > 0 && totalPages > 1 && (
        <div className="cohort-members-pager">
          <button
            type="button"
            className="cohort-members-pager-button"
            aria-label={intl.formatMessage(messages.pagerPrevious)}
            disabled={previous === null || loading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <span aria-hidden="true">‹</span>
          </button>
          <span className="cohort-members-pager-status">
            {intl.formatMessage(messages.pagerStatus, { page, total: totalPages })}
          </span>
          <button
            type="button"
            className="cohort-members-pager-button"
            aria-label={intl.formatMessage(messages.pagerNext)}
            disabled={next === null || loading}
            onClick={() => setPage((current) => current + 1)}
          >
            <span aria-hidden="true">›</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CohortMembers;
